/**
 * Top navigation shared across the 14 screens.
 *
 * Two modes:
 *   - "signup"  → wordmark + "Sign in" link (right). Used on screens
 *                 01 / 02 / 03a / 03b / 15 (anything pre-activation).
 *   - "app"     → full Pro shell with mode switch (IFA only), nav
 *                 links, market clock, icon buttons, user pill.
 *
 * `scope` is "ifa" | "individual" — only meaningful for app mode.
 *
 * Mounted once at the root of each screen rather than in AppShell —
 * the design has subtle padding differences per screen so leaving it
 * to the consumer matches the source mockup pixel-for-pixel.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCredits } from "@/lib/credits";
import { AiCreditsPill } from "@/components/AiCreditsPill";
import { useAuth } from "@/auth";
import { useRecordsMode } from "@/lib/routes";
import { getDataAsOf } from "@/lib/api";

const _MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Format an ISO date ("2026-06-19") to "19 Jun" without a timezone shift.
function fmtAsOf(d: string | null): string | null {
  if (!d) return null;
  const [, m, day] = d.split("-").map(Number);
  return _MON[m - 1] ? `${day} ${_MON[m - 1]}` : null;
}

interface AppNavProps {
  mode: "signup" | "signin" | "app";
  scope?: "ifa" | "individual";
  signOutHref?: string;
  signInHref?: string;
  signUpHref?: string;
  /** Replace the right-side link with "Sign out" on pre-activation screens
   *  that still have an auth session (IFAVerify, ApprovalPending). */
  showSignOut?: boolean;
}

export function AppNav({
  mode,
  scope,
  signInHref = "/signin",
  signOutHref = "/signout",
  signUpHref = "/signup",
  showSignOut = false,
}: AppNavProps) {
  const records = useRecordsMode();
  if (mode === "signup" || mode === "signin") {
    const isSignIn = mode === "signin";
    return (
      <header className="appnav" style={{ padding: "18px 32px" }}>
        <Wordmark />
        <div style={{ flex: 1 }} />
        {showSignOut ? (
          <a
            href={signOutHref}
            style={{
              color: "var(--ink-mid)",
              textDecoration: "none",
              fontSize: 13,
            }}
          >
            Sign out
          </a>
        ) : isSignIn ? (
          <a
            href={signUpHref}
            style={{
              color: "var(--ink-mid)",
              textDecoration: "none",
              fontSize: 13.5,
            }}
          >
            New here?{" "}
            <span
              style={{
                color: "var(--brand)",
                fontWeight: 500,
                marginLeft: 6,
              }}
            >
              Create an account
            </span>
          </a>
        ) : (
          <a
            href={signInHref}
            style={{
              color: "var(--ink-mid)",
              textDecoration: "none",
              fontSize: 13.5,
            }}
          >
            Already have an account?{" "}
            <span
              style={{
                color: "var(--brand)",
                fontWeight: 500,
                marginLeft: 6,
              }}
            >
              Sign in
            </span>
          </a>
        )}
      </header>
    );
  }

  // Modeswitch visibility tracks the user's ROLE in the auth profile,
  // NOT the screen's scope prop. Without this, an IFA navigating to
  // their personal portfolio (/home — which passes scope="individual")
  // loses the modeswitch and has no way back to /clients.
  const { profile } = useAuth();
  // Both advisor personas (IFA/RIA + MFD) get the client book + persona switch;
  // retail does not.
  const isAdvisorByRole = profile?.role === "ifa" || profile?.role === "mfd" || profile?.role === "ra";
  // For active-nav highlighting we still respect the screen's scope so
  // the "Portfolio"/"Clients" link reflects what the user is currently
  // looking at.
  const isIFA = scope === "ifa";
  return (
    <header className="appnav">
      <Wordmark />
      {isAdvisorByRole && <IFAModeSwitch />}
      <NavLinks isIFA={isIFA} isAdvisor={isAdvisorByRole} />
      <div style={{ flex: 1 }} />
      <MarketClock />
      {/* Two wallets, two pills: briefing credits (IFA only) and the AI
          credit wallet everyone else spends on Ask / health reports. */}
      {!records && <><CreditsPill /><AiCreditsPill /></>}
      <UserPill />
    </header>
  );
}

/**
 * Clickable brand wordmark. Click destinations:
 *   - Authenticated active user → workspace home (role-aware)
 *   - Authenticated but pending/no-profile → /onboarding/pending
 *   - Unauthenticated → / (landing page)
 *
 * Centralised so both the app-mode header and the signin/signup header
 * route the same way when a user clicks the logo. The legacy block was
 * a plain <div> with no onClick — users instinctively click the logo
 * to "go home" and got nothing.
 */
function Wordmark() {
  const records = useRecordsMode();
  const nav = useNavigate();
  const { user, profile } = useAuth();
  const go = () => {
    if (!user) {
      nav("/");
      return;
    }
    if (profile?.status === "active") {
      nav(profile.role === "individual" ? "/home" : "/clients");
      return;
    }
    // Pending / new sign-up — let LandingRedirect at / decide.
    nav("/");
  };
  return (
    <div
      className="wordmark"
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          go();
        }
      }}
      style={{ cursor: "pointer" }}
      aria-label="Home"
    >
      <span className="bm">S</span>
      {records ? <>Portfolio <span className="pro">Records</span></> : <>StockSense <span className="ai">AI</span> <span className="pro">Pro</span></>}
    </div>
  );
}

/**
 * IFA persona switcher — toggles between the client book (/clients)
 * and the IFA's OWN personal portfolio (/home, same screen Individual
 * users see). Active tab tracks the current route so navigating
 * elsewhere updates the chip.
 */
function IFAModeSwitch() {
  const nav = useNavigate();
  const loc = useLocation();
  const inPortfolio = loc.pathname === "/home" || loc.pathname.startsWith("/home/");
  return (
    <div className="modeswitch" role="tablist">
      <button
        className={!inPortfolio ? "active" : ""}
        role="tab"
        onClick={() => nav("/clients")}
      >
        <span className="dot" />
        My clients
      </button>
      <button
        className={inPortfolio ? "active" : ""}
        role="tab"
        onClick={() => nav("/home")}
      >
        My portfolio
      </button>
    </div>
  );
}

/**
 * Top nav links. The "Clients" / "Portfolio" link is wired and routes
 * to the user's workspace home. Research / Watchlist / Alerts are
 * aspirational — there are no dedicated screens for them yet (alerts
 * surface inline on the IFA dashboard; watchlists aren't built). They
 * stay visible (so the nav matches the design spec) but render disabled
 * with a tooltip so users stop clicking dead links.
 */
// Owner-only surfaces. Mirrors the RequireAdmin gate in App.tsx and the
// server-side ADMIN_EMAILS check — this only decides whether the LINK shows.
const ADMIN_EMAILS = ["murali.adhikarla@gmail.com"];

function NavLinks({ isIFA, isAdvisor }: { isIFA: boolean; isAdvisor: boolean }) {
  const records = useRecordsMode();
  const nav = useNavigate();
  const loc = useLocation();
  const { profile: navProfile } = useAuth();
  const isAdmin = ADMIN_EMAILS.includes((navProfile?.email || "").toLowerCase());
  // For IFAs, the home link still says "Clients" — they switch personas
  // via the modeswitch above, not via this nav link.
  const home = isIFA ? "/clients" : "/home";
  const onHome =
    loc.pathname === home ||
    loc.pathname === home + "/empty" ||
    loc.pathname.startsWith(home + "/");
  const homeLabel = isIFA ? "Clients" : "Portfolio";
  const [moreOpen, setMoreOpen] = useState(false);
  // Close on route change and on Escape — a menu that survives navigation
  // hangs over the screen you just moved to.
  useEffect(() => setMoreOpen(false), [loc.pathname]);
  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMoreOpen(false);
    const onClick = () => setMoreOpen(false);
    document.addEventListener("keydown", onKey);
    // Capture phase so a click anywhere (including another nav item) closes it,
    // while the menu's own buttons still run their onClick first.
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onClick);
    };
  }, [moreOpen]);

  // F17 / F18 — destinations as DATA, split by how often they are used.
  //
  // This was nine flat links for an admin adviser (Clients, Funds, Practice,
  // Prospect, Reports, Plan, Admin, Market, Alerts), all weighted equally. Two
  // problems came out of that. On desktop nothing said which of the nine was
  // the place to start a piece of work, and Book/Practice/Alerts each showed
  // overlapping exposure and concentration. On a phone the row became a
  // horizontally scrolling strip with `scrollbar-width: none`, so Alerts sat
  // past the right edge with NO affordance that anything was there — a
  // destination you can only reach by guessing that the bar scrolls is not
  // navigable.
  //
  // PRIMARY is the daily loop: the book, research, what came back, what needs
  // attention. Everything else is real but occasional, and lives behind a
  // labelled More menu that is discoverable at every width.
  const primary: { to: string; label: string; show: boolean }[] = [
    { to: home, label: homeLabel, show: true },
    { to: "/funds", label: "Funds", show: isAdvisor },
    { to: "/watchlist", label: "Watchlist", show: !isIFA },
    { to: "/reports", label: "Reports", show: isAdvisor },
    { to: "/alerts", label: "Alerts", show: true },
  ];
  const secondary: { to: string; label: string; show: boolean; hint?: string }[] = [
    { to: "/market", label: "Market", show: true,
      hint: "Breadth, sector rotation, active vs passive" },
    { to: "/library", label: "My research", show: true, hint: "Saved Deep Dives and dossiers" },
    { to: "/practice", label: "Practice", show: isAdvisor,
      hint: "Your whole book as one portfolio" },
    { to: "/prospect", label: "Prospect", show: isAdvisor,
      hint: "Turn a prospect's holdings into a proposal" },
    { to: "/compliance", label: "Compliance", show: navProfile?.role === "ifa",
      hint: "Risk profiles, suitability, fees and your audit pack" },
    { to: "/plan", label: "Plan", show: isAdvisor,
      hint: "Your subscription and allowances" },
    { to: "/subscribe", label: "Subscription / Go Pro", show: navProfile?.role === "individual",
      hint: "View Pro plans, renew or manage your subscription" },
    { to: "/admin", label: "Admin", show: isAdmin,
      hint: "AI usage across all models (owner only)" },
  ];
  const recordsPrimary = [
    { to: home, label: isIFA ? "Clients" : "Records", show: true },
    { to: "/inbox", label: "Statement inbox", show: true },
    { to: isAdvisor ? "/plan" : "/subscribe", label: "Subscription", show: true },
  ];
  const visibleSecondary = secondary.filter((x) => x.show && (!records || x.to === "/admin" || x.to === "/compliance"));
  const inMore = visibleSecondary.some((x) => loc.pathname === x.to);

  return (
    <nav className="navlinks nav-hide-mobile">
      {(records ? recordsPrimary : primary).filter((x) => x.show).map((x) => (
        <a
          key={x.to}
          className={
            (x.to === home ? onHome : loc.pathname === x.to) ? "active" : ""
          }
          onClick={() => nav(x.to)}
          style={{ cursor: "pointer" }}
        >
          {x.label}
        </a>
      ))}
      {!records && !isAdvisor && (
        <DisabledNavLink
          label="Research"
          tooltip="Coming soon — for now use the ticker search on your home screen"
        />
      )}
      {visibleSecondary.length > 0 && (
        <div className="nav-more">
          <button
            type="button"
            className={"nav-more-btn" + (inMore ? " active" : "")}
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            onClick={(e) => {
              e.stopPropagation();
              setMoreOpen((v) => !v);
            }}
          >
            More <span aria-hidden>{moreOpen ? "▴" : "▾"}</span>
          </button>
          {moreOpen && (
            <div className="nav-more-menu" role="menu">
              {visibleSecondary.map((x) => (
                <button
                  key={x.to}
                  type="button"
                  role="menuitem"
                  className={loc.pathname === x.to ? "active" : ""}
                  title={x.hint}
                  onClick={() => {
                    setMoreOpen(false);
                    nav(x.to);
                  }}
                >
                  {x.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </nav>
  );
}

function DisabledNavLink({ label, tooltip }: { label: string; tooltip: string }) {
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(false), 2800);
    return () => clearTimeout(t);
  }, [flash]);
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      <a
        aria-disabled="true"
        title={tooltip}
        onClick={(e) => { e.preventDefault(); setFlash(true); }}
        style={{ opacity: 0.5, cursor: "not-allowed", display: "inline-flex", alignItems: "center", gap: 5 }}
      >
        {label}
        <span style={{
          fontSize: 8.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase",
          color: "var(--brand)", border: "1px solid var(--brand)", borderRadius: 4, padding: "1px 4px",
        }}>Soon</span>
      </a>
      {flash && (
        <span role="status" style={{
          position: "absolute", top: "100%", left: 0, marginTop: 6, zIndex: 40,
          background: "var(--ink)", color: "#fff", fontSize: 11, lineHeight: 1.4,
          padding: "6px 9px", borderRadius: 6, width: 220, boxShadow: "0 6px 18px rgba(0,0,0,.25)",
        }}>
          {tooltip}
        </span>
      )}
    </span>
  );
}

/**
 * Market clock. Computed client-side from IST — no backend roundtrip
 * for something every browser can derive from the wall clock.
 *
 * State buckets:
 *   · open       09:15-15:30 IST, weekday
 *   · pre-open   09:00-09:15 IST, weekday
 *   · closed     all other times (incl. Saturday/Sunday)
 *
 * The previous version showed a hardcoded "NIFTY 24,612 +0.42%" pill.
 * We don't have a live NIFTY 50 feed in /public/ticker-tape (that
 * endpoint returns individual stocks for the marketing landing page),
 * so the NIFTY block was removed entirely rather than re-fabricating it.
 * Bringing it back means either adding NIFTY to _MARKETING_TICKERS or
 * a dedicated yfinance index endpoint.
 */
function MarketClock() {
  const [, force] = useState(0);
  const [asOf, setAsOf] = useState<string | null>(null);
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    getDataAsOf().then((r) => setAsOf(r.equity_as_of)).catch(() => {});
  }, []);

  const istNow = new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date());

  const minutes = (() => {
    const [h, m] = istNow.split(":").map(Number);
    return h * 60 + m;
  })();
  const dayIdx = new Date().toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    weekday: "short",
  });
  const isWeekday = !/sat|sun/i.test(dayIdx);
  const openMin = 9 * 60 + 15;
  const closeMin = 15 * 60 + 30;
  const preOpenMin = 9 * 60;

  let state: "open" | "preopen" | "closed" = "closed";
  if (isWeekday) {
    if (minutes >= openMin && minutes < closeMin) state = "open";
    else if (minutes >= preOpenMin && minutes < openMin) state = "preopen";
  }
  const dotColor =
    state === "open"
      ? "var(--gain-text)"
      : state === "preopen"
        ? "var(--warn-text)"
        : "var(--ink-vlight)";
  const label =
    state === "open" ? "open" : state === "preopen" ? "pre-open" : "closed";

  return (
    <div
      className="row gap-2 center nav-hide-mobile"
      style={{
        fontFamily: "var(--mono)",
        fontSize: 11.5,
        color: "var(--ink-light)",
        letterSpacing: ".06em",
        textTransform: "uppercase",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: dotColor,
        }}
      />
      <span>NSE · {istNow} IST · {label}{fmtAsOf(asOf) ? ` · data ${fmtAsOf(asOf)}` : ""}</span>
    </div>
  );
}

/**
 * User pill with dropdown menu. Click the pill → opens a small menu
 * with Landing / Sign out. Sign out clears Firebase auth and routes
 * to the landing page (not the sign-in screen — sign-out is a graceful
 * exit, sign-in is the next entry).
 *
 * The previous version was a static <div> — users had no way to log
 * out from inside the app.
 */
function UserPill() {
  const { profile, user, signOut } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const name = profile?.displayName || user?.displayName || user?.email || "User";
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "U";

  // Click-outside close. Listens once mounted; cleans up on unmount.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    // Defer the listener attach to next tick so the click that OPENED
    // the menu doesn't immediately close it.
    const id = setTimeout(() => window.addEventListener("click", close), 0);
    return () => {
      clearTimeout(id);
      window.removeEventListener("click", close);
    };
  }, [open]);

  const onSignOut = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(false);
    try {
      await signOut();
    } catch (err) {
      console.warn("[signout] failed:", err);
    }
    // Land on the marketing page after sign-out — feels like a graceful
    // exit; the user can sign back in via the nav button on landing.
    nav("/", { replace: true });
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        className="user"
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        style={{ cursor: "pointer" }}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span>{name}</span>
        <span className="av sm">{initials}</span>
      </div>
      {open && (
        <div
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            minWidth: 180,
            background: "var(--paper)",
            border: "1px solid var(--line)",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            padding: 6,
            zIndex: 100,
            fontSize: 13,
          }}
        >
          <button
            onClick={() => {
              setOpen(false);
              nav("/landing");
            }}
            style={menuItemStyle}
          >
            About / Landing
          </button>
          <div
            style={{
              height: 1,
              background: "var(--line-soft)",
              margin: "4px 0",
            }}
          />
          <button onClick={onSignOut} style={menuItemStyle}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

const menuItemStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "8px 12px",
  background: "transparent",
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 13,
  color: "var(--ink-mid)",
  fontFamily: "inherit",
};

/**
 * Credits balance pill — sits in AppNav between the market clock and
 * the user avatar. Reads from CreditsContext, click navigates to
 * /billing/packs. Hidden while the first load is in flight to avoid
 * a flash of "0 credits" for users who actually have credits.
 */
function CreditsPill() {
  const { credits, loading } = useCredits();
  const { profile } = useAuth();
  const nav = useNavigate();
  // BRIEFING credits, and equity briefings are IFA-only (`_require_roles(uid,
  // {"ifa"})` on every generation path). Showing this to retail or an MFD
  // advertised a shop selling something they can never spend.
  if ((profile?.role !== "ifa" && profile?.role !== "ra")) return null;
  // Never assert a balance we haven't loaded — `credits ?? 0` rendered a false
  // "0 credits" during hydration and drove support tickets (audit P1-4b).
  if (credits == null || loading) return null;
  const low = credits <= 1;
  return (
    <button
      onClick={() => nav("/billing/packs")}
      className="row gap-2 center"
      title={low ? "Low balance — buy more credits" : "Buy more credits"}
      style={{
        background: low ? "var(--warn-bg)" : "var(--paper-2)",
        color: low ? "var(--warn-text)" : "var(--ink-mid)",
        border: "1px solid var(--line)",
        padding: "5px 11px",
        borderRadius: 999,
        fontFamily: "var(--mono)",
        fontSize: 12.5,          // was 11.5 — below the 12px caption floor
        letterSpacing: ".06em",
        textTransform: "uppercase",
        cursor: "pointer",
        marginRight: 10,
        minHeight: 32,           // tap target
        gap: 5,                  // the two spans rendered as "0credits"
      }}
    >
      {/* "report credits" — the purchasable BRIEFING pool (₹100 each), distinct
          from the retail AI-credit wallet (₹5 each). Two pools sharing the bare
          word "credits" made a 90-balance and a 0-balance look contradictory. */}
      <span>{credits}</span>
      <span>{credits === 1 ? "report credit" : "report credits"}</span>
    </button>
  );
}
